import React, { useMemo, useCallback } from "react";
import { Container, Text, Sprite } from "@pixi/react";
import { TextStyle, TextMetrics } from "pixi.js";
import * as PIXI from "pixi.js";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
} from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";
import {
  getFeature1SlideTextConfig,
  getFeature2SlideTextConfig,
  getFeature3SlideTextConfig,
} from "./paytableTextConfig";

const FeaturesSlide = ({ viewportConfig, slideIndex }) => {
  const { t } = useTranslation();
  const { uiSymbolScale, getResponsiveConfig, device, orientation } =
    useDimensions();

  // Get button config for responsive spacing
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG),
    [getResponsiveConfig]
  );

  // Device-specific text configuration for each individual feature slide
  const textConfig = useMemo(() => {
    // slideIndex 2 = Feature 1, slideIndex 3 = Feature 2, slideIndex 4 = Feature 3
    if (slideIndex === 2) {
      return getFeature1SlideTextConfig(device, orientation);
    } else if (slideIndex === 3) {
      return getFeature2SlideTextConfig(device, orientation);
    } else if (slideIndex === 4) {
      return getFeature3SlideTextConfig(device, orientation);
    }
    // Fallback to Feature 1 config if slideIndex is unexpected
    return getFeature1SlideTextConfig(device, orientation);
  }, [slideIndex, device, orientation]);

  // Calculate subtitle font size
  const baseSubtitleFontSize = 46;
  const subtitleFontSize = useMemo(
    () =>
      Math.round(
        baseSubtitleFontSize *
          viewportConfig.titleFontSizeMultiplier *
          uiSymbolScale
      ),
    [viewportConfig.titleFontSizeMultiplier, uiSymbolScale]
  );

  // Subtitle text style - yellow color
  const subtitleStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: subtitleFontSize,
        fontWeight: "bold",
        fill: 0xfeff00, // Yellow color
        align: "center",
      }),
    [subtitleFontSize]
  );

  // Determine which feature to show based on slideIndex
  // slideIndex 2 = feature 1, slideIndex 3 = feature 2, slideIndex 4 = feature 3
  const featureNumber = slideIndex - 1; // 1, 2, or 3
  const featureAssetName = `feature_0${featureNumber}_graphics`;
  const featureTitleKey = `feature_0${featureNumber}_image_title`;
  const featureDescriptionKey = `feature_0${featureNumber}_image_description`;

  // Get feature image texture (similar to ReelSet.jsx)
  const featureTexture = PIXI.Assets.get(featureAssetName);

  // Get shine texture for landscape mode
  const shineTexture = useMemo(() => {
    return PIXI.Assets.get("shine");
  }, []);

  // Calculate shine dimensions maintaining aspect ratio
  const shineDimensions = useMemo(() => {
    if (!shineTexture) return { width: 0, height: 0 };
    const nativeWidth = shineTexture.width;
    const nativeHeight = shineTexture.height;
    const scaledWidth = nativeWidth * uiSymbolScale;
    const scaledHeight = nativeHeight * uiSymbolScale;
    return { width: scaledWidth, height: scaledHeight };
  }, [shineTexture, uiSymbolScale]);

  // Determine layout: portrait = 1 column (mobile/tablet portrait), landscape = 2 columns (everything else)
  const isPortrait =
    (device === PixiSettings.DEVICE.MOBILE ||
      device === PixiSettings.DEVICE.TABLET) &&
    orientation === PixiSettings.ORIENTATION.PORTRAIT;

  // Calculate subtitle position
  const subtitleY = subtitleFontSize * 0.3;

  // Calculate spacing after subtitle
  const subtitleSpacing = buttonConfig.height * 0.8;

  // Calculate available space for content
  const availableHeight =
    viewportConfig.viewportHeight - subtitleY - subtitleFontSize - subtitleSpacing;

  // Get content max width from config
  const contentMaxWidth = useMemo(
    () => viewportConfig.maxWidth * textConfig.columnWidthMultiplier,
    [viewportConfig.maxWidth, textConfig.columnWidthMultiplier]
  );

  // Get image max width (larger than text width in landscape)
  const getImageMaxWidth = useCallback(() => {
    if (isPortrait) {
      return viewportConfig.maxWidth * 0.75;
    } else {
      return viewportConfig.maxWidth * 0.5; // Increased from 0.45
    }
  }, [isPortrait, viewportConfig.maxWidth]);

  const imageMaxWidth = useMemo(
    () => getImageMaxWidth(),
    [getImageMaxWidth]
  );

  // Calculate image dimensions maintaining aspect ratio
  const imageDimensions = useMemo(() => {
    if (!featureTexture) return { width: 0, height: 0 };

    const aspectRatio = featureTexture.width / featureTexture.height;

    // Max size for the image
    const maxImageWidth = imageMaxWidth * 0.95; // Use larger image width
    const maxImageHeight = availableHeight * 0.7; // Increased from 0.6

    // Calculate dimensions maintaining aspect ratio
    let imageWidth = maxImageWidth;
    let imageHeight = imageWidth / aspectRatio;

    // If height exceeds max, scale down based on height
    if (imageHeight > maxImageHeight) {
      imageHeight = maxImageHeight;
      imageWidth = imageHeight * aspectRatio;
    }

    return {
      width: Math.round(imageWidth),
      height: Math.round(imageHeight),
    };
  }, [featureTexture, imageMaxWidth, availableHeight]);

  // Content start Y position
  const contentStartY = subtitleY + subtitleFontSize + subtitleSpacing;

  // Calculate vertical center position for landscape layout
  const contentCenterY = contentStartY + availableHeight / 2;

  // Calculate base font sizes using device-specific config
  const baseTitleFontSize = subtitleFontSize * textConfig.titleFontMultiplier;
  const baseDescriptionFontSize = subtitleFontSize * textConfig.descriptionFontMultiplier;

  // Get translated texts for measurement
  const titleText = t(featureTitleKey);
  const descriptionText = t(featureDescriptionKey);

  // AUTO-SCALING: Title font size with overflow prevention
  // Ensures title fits within max rows and doesn't overflow
  const finalTitleFontSize = useMemo(() => {
    if (!viewportConfig || !contentMaxWidth) return baseTitleFontSize;

    const MAX_TITLE_ROWS = 4; // Maximum rows allowed for title (increased from 3)
    const MIN_FONT_SIZE = 12; // Minimum readable font size
    let fontSize = baseTitleFontSize;
    let iterations = 0;
    const maxIterations = 20;

    while (iterations < maxIterations) {
      // Create test style with current font size
      const testStyle = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: fontSize,
        fontWeight: "bold",
        wordWrap: true,
        wordWrapWidth: contentMaxWidth,
        breakWords: true,
        lineHeight: Math.round(fontSize * 1.2),
      });

      const metrics = TextMetrics.measureText(titleText, testStyle);
      const lineHeight = metrics.lineHeight || fontSize * 1.2;
      const actualRows = Math.ceil(metrics.height / lineHeight);

      // Check if text fits within max rows
      if (actualRows <= MAX_TITLE_ROWS) {
        break;
      }

      // Check if we've hit minimum font size
      if (fontSize <= MIN_FONT_SIZE) {
        break;
      }

      // Scale down by 5% and try again
      fontSize = Math.round(fontSize * 0.95);
      iterations++;
    }

    return fontSize;
  }, [viewportConfig, contentMaxWidth, baseTitleFontSize, titleText]);

  // Calculate gap between texts based on final title size
  const gapBetweenTexts = finalTitleFontSize * textConfig.gapMultiplier;

  // Create title style with auto-scaled font size
  const titleStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalTitleFontSize,
        fontWeight: "bold",
        fill: 0xffffff,
        align: "center",
        wordWrap: true,
        wordWrapWidth: contentMaxWidth,
        breakWords: true,
        lineHeight: Math.round(finalTitleFontSize * 1.2),
      }),
    [finalTitleFontSize, contentMaxWidth]
  );

  // Measure actual title height with final scaled font
  const actualTitleHeight = useMemo(() => {
    const metrics = TextMetrics.measureText(titleText, titleStyle);
    return metrics.height;
  }, [titleText, titleStyle]);

  // AUTO-SCALING: Description font size with overflow prevention
  // Calculate available height for description text
  const availableDescriptionHeight = useMemo(() => {
    // In landscape: text column shares space with image vertically
    // In portrait: text appears below image, so has less vertical space
    if (isPortrait) {
      // Portrait: available height minus image, title, and gaps
      return availableHeight - imageDimensions.height - actualTitleHeight - gapBetweenTexts - subtitleFontSize * 1.5;
    } else {
      // Landscape: more generous height (85% of available height for text block, increased from 70%)
      return availableHeight * 0.85 - actualTitleHeight - gapBetweenTexts;
    }
  }, [isPortrait, availableHeight, imageDimensions.height, actualTitleHeight, gapBetweenTexts, subtitleFontSize]);

  const finalDescriptionFontSize = useMemo(() => {
    if (!viewportConfig || !contentMaxWidth || availableDescriptionHeight <= 0) {
      return baseDescriptionFontSize;
    }

    const MAX_DESCRIPTION_ROWS = isPortrait ? 8 : 12; // Increased: more rows allowed in landscape
    const MIN_FONT_SIZE = 12; // Minimum readable font size
    let fontSize = baseDescriptionFontSize;
    let iterations = 0;
    const maxIterations = 20;

    while (iterations < maxIterations) {
      // Create test style with current font size
      const testStyle = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: fontSize,
        fontWeight: "400",
        wordWrap: true,
        wordWrapWidth: contentMaxWidth,
        breakWords: true,
        lineHeight: Math.round(fontSize * 1.3),
      });

      const metrics = TextMetrics.measureText(descriptionText, testStyle);
      const lineHeight = metrics.lineHeight || fontSize * 1.3;
      const actualRows = Math.ceil(metrics.height / lineHeight);

      // Check if text fits within max rows AND available height
      if (actualRows <= MAX_DESCRIPTION_ROWS && metrics.height <= availableDescriptionHeight) {
        break;
      }

      // Check if we've hit minimum font size
      if (fontSize <= MIN_FONT_SIZE) {
        break;
      }

      // Scale down by 5% and try again
      fontSize = Math.round(fontSize * 0.95);
      iterations++;
    }

    return fontSize;
  }, [viewportConfig, contentMaxWidth, baseDescriptionFontSize, descriptionText, availableDescriptionHeight, isPortrait]);

  // Create description style with auto-scaled font size
  const descriptionStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalDescriptionFontSize,
        fontWeight: "400",
        fill: 0xffffff,
        align: "center",
        wordWrap: true,
        wordWrapWidth: contentMaxWidth,
        breakWords: true,
        lineHeight: Math.round(finalDescriptionFontSize * 1.3),
      }),
    [finalDescriptionFontSize, contentMaxWidth]
  );

  // Measure actual description height with final scaled font
  const actualDescriptionHeight = useMemo(() => {
    const metrics = TextMetrics.measureText(descriptionText, descriptionStyle);
    return metrics.height;
  }, [descriptionText, descriptionStyle]);

  // Calculate total text block height using actual measured heights
  const totalTextBlockHeight = actualTitleHeight + gapBetweenTexts + actualDescriptionHeight;

  // Calculate positions for portrait mode (image first, then text)
  // Add offset to push content towards the middle using device-specific config
  const portraitTopOffset = availableHeight * textConfig.portraitTopOffset;
  const landscapeTopOffset = availableHeight * textConfig.landscapeTopOffset;
  const imageYPortrait = contentStartY + portraitTopOffset + imageDimensions.height / 2;
  const textYPortrait = contentStartY + portraitTopOffset + imageDimensions.height + subtitleFontSize * 0.8; // Gap after image

  // Calculate text container Y position
  // In landscape: center the text block to align with the image center (both at contentCenterY)
  // In portrait: text appears below image
  const textContainerY = isPortrait
    ? textYPortrait
    : contentCenterY - totalTextBlockHeight / 2 + landscapeTopOffset;

  // Guard: Don't render until texture is loaded (after all hooks and calculations)
  if (!featureTexture || !shineTexture) {
    return null;
  }

  return (
    <Container name={`secondary_features_slide_${slideIndex}`}>
      {/* Subtitle */}
      <Text
        text={t("features_title")}
        style={subtitleStyle}
        anchor={{ x: 0.5, y: 0 }}
        x={0}
        y={Math.round(subtitleY)}
        resolution={2}
      />

      {/* Shine Image - Only visible in landscape, centered between text and image */}
      {!isPortrait && shineTexture && (
        <Sprite
          texture={shineTexture}
          anchor={0.5}
          x={0}
          y={Math.round(contentCenterY + landscapeTopOffset)}
          width={shineDimensions.width}
          height={shineDimensions.height}
        />
      )}

      {/* Text Container - Left side in landscape, centered in portrait */}
      <Container
        name="feature_text_container"
        x={isPortrait ? 0 : Math.round(-viewportConfig.maxWidth / 4)}
        y={Math.round(textContainerY)}
      >
        {/* Feature Title */}
        <Text
          text={titleText}
          style={titleStyle}
          anchor={{ x: 0.5, y: 0 }}
          x={0}
          y={0}
          resolution={2}
        />

        {/* Feature Description */}
        <Text
          text={descriptionText}
          style={descriptionStyle}
          anchor={{ x: 0.5, y: 0 }}
          x={0}
          y={Math.round(actualTitleHeight + gapBetweenTexts)}
          resolution={2}
        />
      </Container>

      {/* Feature Image - Right side in landscape, top in portrait */}
      {featureTexture && (
        <Sprite
          texture={featureTexture}
          anchor={0.5}
          x={isPortrait ? 0 : viewportConfig.maxWidth / 4}
          y={Math.round(
            isPortrait ? imageYPortrait : contentCenterY + landscapeTopOffset
          )}
          width={imageDimensions.width}
          height={imageDimensions.height}
        />
      )}
    </Container>
  );
};

export default FeaturesSlide;
